import { useAuthorization } from '@shared/auth/use-authorization';
import { keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper, PaginationState, SortingState } from '@tanstack/react-table';
import { isEmpty, isNil } from 'lodash';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { HiMagnifyingGlass, HiOutlinePlus, HiOutlineSquares2X2, HiOutlineTrash } from 'react-icons/hi2';

import { Breadcrumbs } from '@/components-new/breadcrumbs';
import { Button } from '@/components-new/button';
import { ConfirmDeleteDialog } from '@/components-new/confirm-delete-dialog';
import { DataPaginator } from '@/components-new/data-paginator';
import { DataTable } from '@/components-new/data-table';
import { useDataTable } from '@/components-new/data-table/use-data-table';
import { EmptyState, EmptyStateBody, EmptyStateHeading } from '@/components-new/empty-state';
import { Field, FieldGroup } from '@/components-new/fieldset';
import { Input, InputGroup } from '@/components-new/input';
import { Loader } from '@/components-new/loader';
import { Overlay } from '@/components-new/overlay';
import { Page } from '@/components-new/page';
import { PageTitleRow } from '@/components-new/page-title-row';
import { TextLink } from '@/components-new/text';
import { useClassificationsQuery, useDeleteClassification } from '@/features/classifications/api';
import { Classification } from '@/features/classifications/types/classification';
import { useDebounceValue } from '@/hooks/use-debounce-value';
import { useUnmount } from '@/hooks/use-unmount';
import { insertIf } from '@/utils/arrays';

const columnHelper = createColumnHelper<Classification>();

type FilterForm = {
  name?: string;
};

const ClassificationsPage = () => {
  const { checkPolicies } = useAuthorization();
  const canManageClassifications = checkPolicies(['canManageClassifications']);

  const filtersWatchSubscriptionRef = React.useRef<{ unsubscribe: () => void } | undefined>();
  const filtersForm = useForm<FilterForm>();
  const [filters, setFilters] = useDebounceValue(filtersForm.getValues(), 350);
  const filtersEmpty = Object.values(filters).every(isEmpty);
  const { watch } = filtersForm;

  const [classificationForDelete, setClassificationForDelete] = React.useState<Classification | undefined>();

  React.useEffect(() => {
    filtersWatchSubscriptionRef.current = watch(setFilters);
  }, [setFilters, watch]);

  useUnmount(() => {
    if (filtersWatchSubscriptionRef.current) {
      filtersWatchSubscriptionRef.current.unsubscribe();
    }
  });

  const {
    data: classifications,
    isFetching: isLoadingClassifications,
  } = useClassificationsQuery({
    queryConfig: { placeholderData: keepPreviousData },
  });

  const filteredClassifications = useMemo(() => {
    if (!classifications) return [];
    const { name } = filters;
    if (!name) return classifications;
    const lower = name.toLowerCase();
    return classifications.filter(c => c.name.toLowerCase().includes(lower));
  }, [classifications, filters]);

  const showLoader = isLoadingClassifications;
  const hasData = !isEmpty(filteredClassifications) && !isNil(filteredClassifications);
  const showEmptyState = !hasData && !showLoader;

  const {
    mutate: deleteClassification,
    isPending: isDeletingClassification,
  } = useDeleteClassification();

  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'name', desc: false }]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Name',
      sortDescFirst: false,
      cell: ({ row }) => (
        <TextLink href="/classifications/:id" params={{ id: row.original.id.toString() }}>
          {row.original.name}
        </TextLink>
      ),
    }),
    ...insertIf(
      canManageClassifications,
      columnHelper.display({
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              plain
              title={`Delete ${row.original.name}`}
              aria-label={`Delete ${row.original.name}`}
              onClick={() => setClassificationForDelete(row.original)}
            >
              <HiOutlineTrash/>
            </Button>
          </div>
        ),
      })
    ),
  ], [canManageClassifications]);

  const table = useDataTable({
    data: filteredClassifications,
    columns,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    enableSorting: true,
    enablePagination: true,
    state: { pagination, sorting },
  });

  const handleDelete = (classification: Classification) => {
    deleteClassification(classification, {
      onSuccess: () => setClassificationForDelete(undefined),
    });
  };

  return (
    <Page
      title="Classifications"
      header={({ title }) => (
        <>
          <Breadcrumbs breadcrumbs={[{ label: 'Classifications', url: '/classifications' }]}/>
          <PageTitleRow title={title}>
            {canManageClassifications && (
              <Button
                color="secondary"
                aria-label="Add Classification"
                href="/classifications/new"
              >
                <HiOutlinePlus/>
                Add New
              </Button>
            )}
          </PageTitleRow>
        </>
      )}
    >
      <div className="my-4 flex flex-col justify-between gap-4 sm:flex-row">
        <FieldGroup className="max-w-sm flex-1">
          <Controller
            control={filtersForm.control}
            defaultValue=""
            name="name"
            render={({ field }) => (
              <Field>
                <InputGroup>
                  <HiMagnifyingGlass/>
                  <Input {...field} type="search" placeholder="Search by name"/>
                </InputGroup>
              </Field>
            )}
          />
        </FieldGroup>
      </div>

      <DataTable table={table}/>
      {(!showLoader || showEmptyState) && <DataPaginator table={table}/>}

      {(showLoader || showEmptyState) && (
        <div className="relative flex h-48 items-center justify-center">
          {showLoader && (
            <Overlay>
              <Loader message="Loading classifications..."/>
            </Overlay>
          )}
          {showEmptyState && (
            <EmptyState>
              <HiOutlineSquares2X2 className="size-12 text-gray-500"/>
              <EmptyStateHeading>No classifications</EmptyStateHeading>
              <EmptyStateBody>
                {filtersEmpty
                  ? 'There are no classifications available.'
                  : 'Try adjusting your search and try again.'}
              </EmptyStateBody>
            </EmptyState>
          )}
        </div>
      )}

      <ConfirmDeleteDialog
        item={classificationForDelete}
        title="Delete Classification"
        message={`Are you sure you want to delete the classification "${classificationForDelete?.name}"?`}
        deleteButtonTitle="Delete Classification"
        isDeleting={isDeletingClassification}
        onClose={() => setClassificationForDelete(undefined)}
        onDeleteConfirmed={handleDelete}
      />
    </Page>
  );
};

export default ClassificationsPage;
